import { memo } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, useBreakpoints } from "@productlane/ui"

import { ConnectorLogo } from "@/components/connector-logo"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { RoleCheck } from "@/components/shared/role-check"
import { useZeroWorkspace } from "@/lib/zero"
import {
	DOCS_SECTIONS,
	useDocNavStore,
	useIsDocsListExpanded,
	useIsDocsSectionOpen,
} from "@/stores/docs/docs-nav-store"
import { DocsNavPlaceholderRow } from "./docs-nav-placeholder-row"
import { DocsShowAllToggle } from "./docs-show-all-toggle"
import { useDelayedUnmount } from "./use-delayed-unmount"

const INITIAL_ROWS = 5
const CONNECTORS_ROUTE = "/settings/custom-connectors"

const ROW_CLASS =
	"flex h-[1.9rem] w-full min-w-0 cursor-default items-center gap-2 rounded-md pl-2 pr-1 text-[13px] font-medium text-secondary hover:bg-gray-100 hover:text-primary focus-visible:bg-gray-100 focus-visible:outline-none hover:dark:bg-gray-800/50 dark:focus-visible:bg-gray-800/50"

// Renders whether or not any connector exists: an empty Sources section is how
// someone finds out the agent can read anything beyond these docs.
export const SourcesSection = memo(() => {
	const workspace = useZeroWorkspace()
	const showAll = useIsDocsListExpanded(DOCS_SECTIONS.sources)
	const isOpen = useIsDocsSectionOpen(DOCS_SECTIONS.sources)
	const toggleSection = useDocNavStore((s) => s.toggleSection)
	const toggleList = useDocNavStore((s) => s.toggleList)
	const renderRows = useDelayedUnmount(isOpen, 250)

	const sources = workspace?.sidebarSections ?? []
	const overflowing = sources.length > INITIAL_ROWS
	const visible = showAll ? sources : sources.slice(0, INITIAL_ROWS)

	return (
		<li className="group/docs-section mt-5">
			<NavSectionHeader
				label="Sources"
				isOpen={isOpen}
				onToggle={() => toggleSection(DOCS_SECTIONS.sources)}
				action={<AddSourceButton />}
				className="pb-1"
			/>
			<div
				inert={!isOpen}
				className={cn(
					"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
					isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-40",
				)}
			>
				<div className="min-h-0 overflow-hidden">
					<ul className="flex list-none flex-col gap-px pb-1">
						{renderRows && (
							<>
								{sources.length === 0 ? (
									<li>
										<DocsNavPlaceholderRow
											label="Connect a source"
											to={CONNECTORS_ROUTE}
										/>
									</li>
								) : (
									visible.map((source) => (
										<li key={source.id}>
											<Link
												to={CONNECTORS_ROUTE}
												title={source.title}
												className={ROW_CLASS}
											>
												<ConnectorLogo
													url={source.url}
													sizeClassName="h-3.5 w-3.5"
													iconClassName="h-3.5 w-3.5"
												/>
												<span className="min-w-0 flex-1 truncate text-ellipsis text-start opacity-90">
													{source.title}
												</span>
											</Link>
										</li>
									))
								)}
								{overflowing && (
									<li>
										<DocsShowAllToggle
											isExpanded={showAll}
											onToggle={() => toggleList(DOCS_SECTIONS.sources)}
										/>
									</li>
								)}
							</>
						)}
					</ul>
				</div>
			</div>
		</li>
	)
})

SourcesSection.displayName = "SourcesSection"

const AddSourceButton = memo(() => {
	const navigate = useNavigate()
	const { isMobile } = useBreakpoints()

	return (
		<RoleCheck not={["VIEWER"]}>
			<IconButton
				Icon={Plus}
				variant="ghost"
				size="sm"
				aria-label="Connect a source"
				onClick={() => void navigate({ href: CONNECTORS_ROUTE })}
				className={cn(
					"flex-none text-tertiary hover:text-primary focus-visible:opacity-100",
					isMobile
						? "opacity-100"
						: "opacity-0 group-hover/section:opacity-100",
				)}
			/>
		</RoleCheck>
	)
})

AddSourceButton.displayName = "AddSourceButton"
